import React, { useState } from 'react';
import moment from 'moment';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Container, Typography, TextField, Box, Button, Slider, Grid, MenuItem,
  Select, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogContentText
} from '@mui/material';
import { saveAs } from 'file-saver';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TimezoneSelector from './TimezoneSelector';

function QRCodeGenerator() {
  const [text, setText] = useState('');
  const [qrCodeSize, setQRCodeSize] = useState(256);
  const [generateType, setGenerateType] = useState('text');
  const [wifiSettings, setWifiSettings] = useState({ ssid: '', password: '', encryption: 'WPA' });
  const [vCard, setVCard] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
  });
  const [GMapsAddress, setGMapsAddress] = useState('');
  const [openLicenseModal, setOpenLicenseModal] = useState(false);
  const [iCalEvent, setICalEvent] = useState({
    eventTitle: '',
    eventLocation: '',
    eventStart: moment(), // Initialize with current date
    eventEnd: moment(), // Initialize with current date
    eventDescription: '',
  });
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [smsNumber, setSmsNumber] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [email, setEmail] = useState({
    emailTo: '',
    emailSubject: '',
    emailBody: '',
  });


  /* Handlers */

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleGMapsChange = (e) => {
    setGMapsAddress(e.target.value);
  };

  const handleQRCodeSizeChange = (e, newValue) => {
    setQRCodeSize(newValue);
  };

  const handleGenerateTypeChange = (event) => {
    setGenerateType(event.target.value);
  };

  const handleWifiSettingsChange = (prop) => (e) => {
    setWifiSettings({ ...wifiSettings, [prop]: e.target.value });
  };

  const handleVCardChange = (prop) => (e) => {
    setVCard({ ...vCard, [prop]: e.target.value });
  };

  const handleICalEventChange = (prop) => (e) => {
    setICalEvent({ ...iCalEvent, [prop]: e.target.value });
  };

  const handleEmailChange = (prop) => (e) => {
    setEmail({ ...email, [prop]: e.target.value });
  };

  const handleOpenLicenseModal = () => {
    setOpenLicenseModal(true);
  };

  const handleCloseLicenseModal = () => {
    setOpenLicenseModal(false);
  };

  const handleTimezoneChange = (timezone) => {
    setSelectedTimezone(timezone);
    console.log("Selected Timezone:", timezone); // Or perform other actions
  };

  const handleWhatsAppNumberChange = (e) => {
    setWhatsAppNumber(e.target.value);
  };

  const handleWhatsAppMessageChange = (e) => {
    setWhatsAppMessage(e.target.value);
  };

  const handleSmsNumberChange = (e) => {
    setSmsNumber(e.target.value);
  };

  const handleSmsMessageChange = (e) => {
    setSmsMessage(e.target.value);
  };

  /* Functions */

  // Correctly format the date and time for the iCal string
  const formatDateForICal = (date) => {
    // Convert the date to UTC and then format it to YYYYMMDDTHHMMSSZ (Z denotes UTC)
    date = moment.tz(date, selectedTimezone);
    date = date.utc().format("YYYYMMDDTHHmmss[Z]");
    //console.log(date);
    return date;
  };

  const generateQRCodeValue = () => {
    switch (generateType) {
      case 'wifi':
        return `WIFI:T:${wifiSettings.encryption};S:${wifiSettings.ssid};P:${wifiSettings.password};;`;
      case 'vcard':
        return `BEGIN:VCARD\nVERSION:3.0\nFN:${vCard.firstName} ${vCard.lastName}\nTEL:${vCard.phone}\nEMAIL:${vCard.email}\nADR:${vCard.address}\nEND:VCARD`;
      case 'gmaps':
        return `https://www.google.com/maps/place/${GMapsAddress}`;
      case 'ical':
        return `BEGIN:VEVENT\nSUMMARY:${iCalEvent.eventTitle}\nLOCATION:${iCalEvent.eventLocation}\nDESCRIPTION:${iCalEvent.eventDescription}\nDTSTART:${formatDateForICal(iCalEvent.eventStart)}\nDTEND:${formatDateForICal(iCalEvent.eventEnd)}\nEND:VEVENT`;
      case 'whatsapp':
        return `https://wa.me/${whatsAppNumber}?text=${encodeURIComponent(whatsAppMessage)}`;
      case 'sms':
        return `SMSTO:${smsNumber}:${encodeURIComponent(smsMessage)}`;
      case 'email':
        return `mailto:${email.emailTo}?subject=${encodeURIComponent(email.emailSubject)}&body=${encodeURIComponent(email.emailBody)}`;
      case 'url':
        return text;
      default:
        return text;
    }
  };

  const saveQRCode = () => {
    const canvas = document.querySelector('canvas');
    canvas.toBlob(blob => {
      saveAs(blob, "QRCode.png");
    });
  };

  /* JSX */

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" gutterBottom>
        QR Code Generator
      </Typography>
      <Box>
        <FormControl fullWidth margin="normal">
          <InputLabel id="generate-type-select-label">Generate Type</InputLabel>
          <Select
            labelId="generate-type-select-label"
            id="generate-type-select"
            value={generateType}
            label="Generate Type"
            onChange={handleGenerateTypeChange}>
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="url">Link (URL)</MenuItem>
            <MenuItem value="email">E-mail</MenuItem>
            <MenuItem value="wifi">WiFi</MenuItem>
            <MenuItem value="vcard">vCard</MenuItem>
            <MenuItem value="ical">iCal</MenuItem>
            <MenuItem value="gmaps">GMaps</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </Select>
        </FormControl>
        {generateType === 'url' && (
          <TextField
            fullWidth
            label="URL"
            variant="outlined"
            margin="normal"
            value={text}
            onChange={handleTextChange}
          />
        )}
        {generateType === 'text' && (
          <TextField
            fullWidth
            label="Text"
            variant="outlined"
            margin="normal"
            value={text}
            onChange={handleTextChange}
          />
        )}
        {generateType === 'email' && (
          <>
            <TextField
              fullWidth
              label="To"
              variant="outlined"
              margin="normal"
              value={email.emailTo}
              onChange={handleEmailChange('emailTo')}
            />
            <TextField
              fullWidth
              label="Subject"
              variant="outlined"
              margin="normal"
              value={email.emailSubject}
              onChange={handleEmailChange('emailSubject')}
            />
            <TextField
              fullWidth
              label="Body"
              variant="outlined"
              margin="normal"
              value={email.emailBody}
              onChange={handleEmailChange('emailBody')}
            />
          </>
        )}
        {generateType === 'sms' && (
          <>
            <TextField
              fullWidth
              label="SMS Number"
              variant="outlined"
              margin="normal"
              value={smsNumber}
              onChange={handleSmsNumberChange}
            />
            <TextField
              fullWidth
              label="SMS Message"
              variant="outlined"
              margin="normal"
              value={smsMessage}
              onChange={handleSmsMessageChange}
            />
          </>
        )}
        {generateType === 'whatsapp' && (
          <>
            <TextField
              fullWidth
              label="WhatsApp Number (with country code)"
              variant="outlined"
              margin="normal"
              value={whatsAppNumber}
              onChange={handleWhatsAppNumberChange}
            />
            <TextField
              fullWidth
              label="Predefined Message"
              variant="outlined"
              margin="normal"
              value={whatsAppMessage}
              onChange={handleWhatsAppMessageChange}
            />
          </>
        )}
        {generateType === 'gmaps' && (
          <TextField
            fullWidth
            label="Address"
            variant="outlined"
            margin="normal"
            value={GMapsAddress}
            onChange={handleGMapsChange}
          />
        )}
        {generateType === 'wifi' && (
          <>
            <TextField
              fullWidth
              label="SSID"
              variant="outlined"
              margin="normal"
              value={wifiSettings.ssid}
              onChange={handleWifiSettingsChange('ssid')}
            />
            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              margin="normal"
              value={wifiSettings.password}
              onChange={handleWifiSettingsChange('password')}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="encryption-select-label">Encryption</InputLabel>
              <Select
                labelId="encryption-select-label"
                id="encryption-select"
                value={wifiSettings.encryption}
                label="Encryption"
                onChange={handleWifiSettingsChange('encryption')}
              >
                <MenuItem value="WPA">WPA/WPA2</MenuItem>
                <MenuItem value="WEP">WEP</MenuItem>
                <MenuItem value="nopass">None</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        {generateType === 'vcard' && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                variant="outlined"
                margin="normal"
                value={vCard.firstName}
                onChange={handleVCardChange('firstName')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                variant="outlined"
                margin="normal"
                value={vCard.lastName}
                onChange={handleVCardChange('lastName')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                margin="normal"
                value={vCard.phone}
                onChange={handleVCardChange('phone')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                margin="normal"
                value={vCard.email}
                onChange={handleVCardChange('email')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                margin="normal"
                value={vCard.address}
                onChange={handleVCardChange('address')}
              />
            </Grid>
          </Grid>
        )}
        {generateType === 'ical' && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                variant="outlined"
                margin="normal"
                value={iCalEvent.eventTitle}
                onChange={handleICalEventChange('eventTitle')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                variant="outlined"
                margin="normal"
                value={iCalEvent.eventLocation}
                onChange={handleICalEventChange('eventLocation')}
              />
            </Grid>
            <Grid item xs={12}>
              <TimezoneSelector onTimezoneChange={handleTimezoneChange} />
            </Grid>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={selectedTimezone}>
              {/* Separate Grid items for each DateTimePicker to add space between them */}
              <Grid item xs={12}>
                <DateTimePicker
                  label="Start Time"
                  value={iCalEvent.eventStart}
                  onChange={(newValue) => {
                    setICalEvent({ ...iCalEvent, eventStart: newValue });
                    //console.log(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
              </Grid>
              <Grid item xs={12}>
                <DateTimePicker
                  label="End Time"
                  value={iCalEvent.eventEnd}
                  onChange={(newValue) => {
                    setICalEvent({ ...iCalEvent, eventEnd: newValue });
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                />
              </Grid>
            </LocalizationProvider>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                margin="normal"
                value={iCalEvent.eventDescription}
                onChange={handleICalEventChange('eventDescription')}
              />
            </Grid>
          </Grid>
        )}


        <Box mt={2}>
          <Typography gutterBottom>QR Code Size</Typography>
          <Slider
            value={qrCodeSize}
            onChange={handleQRCodeSizeChange}
            aria-labelledby="qr-code-size-slider"
            valueLabelDisplay="auto"
            min={128}
            max={512}
          />
        </Box>
        <Box mt={2} textAlign="center">
          <QRCodeCanvas value={generateQRCodeValue()} size={qrCodeSize} level="H" />
        </Box>
        <Box mt={2} display="flex" justifyContent="center">
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => saveQRCode()}>
                Download
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="primary" onClick={() => handleOpenLicenseModal()}>
                License info
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Modal for displaying license information */}
      <Dialog open={openLicenseModal} onClose={handleCloseLicenseModal}>
        <DialogTitle>License Information</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <a target='_blank' rel="noreferrer" href="https://github.com/facebook/react/blob/main/LICENSE">React</a><br />
            <a target='_blank' rel="noreferrer" href="https://github.com/moment/moment/blob/develop/LICENSE">momentjs</a><br />
            <a target='_blank' rel="noreferrer" href="https://github.com/zpao/qrcode.react/blob/trunk/LICENSE">qrcode.react</a><br />
            <a target='_blank' rel="noreferrer" href="https://www.nayuki.io/page/qr-code-generator-library#introduction">Project Nayuki</a><br />
            <a target='_blank' rel="noreferrer" href="https://github.com/mui/material-ui/blob/master/LICENSE">MUI</a><br />
          </DialogContentText>
        </DialogContent>
        <Box textAlign="right" p={2}>
          <Button variant="contained" onClick={handleCloseLicenseModal}>Close</Button>
        </Box>
      </Dialog>

    </Container>
  );
}

export default QRCodeGenerator;
